import { ApolloError } from "@apollo/client";
import {
  Box,
  Flex,
  StyleProps,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useEffect } from "react";

import { IconButton } from "../../../components";
import CopyOutlineIcon from "../../../components/Icons/CopyOutlineIcon";
import {
  CallNoteFragment,
  CallSpeakerFragment,
  CallSpeakerOption,
  TranscriptSegmentFragment,
} from "../../graphql";
import { ClipRange } from "../../types";
import { MediaPlayerInterface } from "../MediaPlayer/useMediaPlayer";
import TranscriptSearch from "../Recording/Transcript/TranscriptSearch";
import TranscriptSegments from "../Recording/Transcript/TranscriptSegments";
import useTranscriptSearch from "../Recording/Transcript/useTranscriptSearch";

export type TranscriptProps = StyleProps & {
  callId?: string;
  transcript: TranscriptSegmentFragment[];
  autoScrollEnabled: boolean;
  speakers: Pick<
    CallSpeakerFragment,
    "label" | "speakerTag" | "isCandidate" | "identified"
  >[];
  player: Pick<
    MediaPlayerInterface,
    "playing" | "time" | "seek" | "play" | "duration"
  >;
  setAutoScrollEnabled(enabled: boolean): void;
  onCreateClipClick?(clipRange: ClipRange): void;
  onCopyTranscript?(): void;
  hideVideo?: () => void;
  focusVideo?: () => void;
  speakerOptions?: Pick<CallSpeakerOption, "id" | "label">[];
  canChangeSpeaker?: boolean;
  isVideoVisible?: boolean;
  isVideoCall?: boolean;
  notesToHighlight?: CallNoteFragment[];
  isSearchEnabled?: boolean;
  isCopyEnabled?: boolean;
  isWarningEnabled?: boolean;
  loading: boolean;
  error?: ApolloError;
};

const Transcript: React.FC<TranscriptProps> = ({
  callId,
  transcript,
  autoScrollEnabled,
  speakers,
  player,
  notesToHighlight,
  setAutoScrollEnabled,
  onCreateClipClick,
  onCopyTranscript,
  hideVideo,
  focusVideo,
  speakerOptions,
  canChangeSpeaker = false,
  isVideoVisible = true,
  isVideoCall = true,
  isSearchEnabled = true,
  isCopyEnabled = true,
  isWarningEnabled = true,
  loading,
  error,
  ...styles
}) => {
  const { onToggle: toggleTranscriptWarning, isOpen: showTranscriptWarning } =
    useDisclosure();

  useEffect(() => {
    if (showTranscriptWarning) {
      window.addEventListener("click", toggleTranscriptWarning);
    }
    return () => {
      window.removeEventListener("click", toggleTranscriptWarning);
    };
  }, [showTranscriptWarning]);

  const {
    calculateSegmentPositions,
    searchTerm,
    setSearchTerm,
    matchPositions,
    segmentPositions,
    activeMatchIndex,
    activeMatchPosition,
    goToNextSearchResult,
    goToPrevSearchResult,
  } = useTranscriptSearch();

  useEffect(() => {
    calculateSegmentPositions(transcript);
  }, [transcript]);

  if (transcript.length === 0) {
    return (
      <Box px="8" py="4" textAlign="center" {...styles}>
        No transcript is available.
      </Box>
    );
  }

  return (
    <Flex direction="column" position="relative" h="100%" {...styles}>
      <Flex
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        position="relative"
        borderBottomColor="gray.100"
        borderBottomWidth="1px"
        pb="16px"
      >
        <TranscriptSearch
          value={searchTerm}
          placeholder="Search"
          onChange={(value) => {
            if (player.playing) {
              setAutoScrollEnabled(false);
            }
            setSearchTerm(value);
          }}
          numMatches={matchPositions.length}
          activeMatchIndex={activeMatchIndex + 1}
          onPrevious={() => {
            goToPrevSearchResult();
            if (player.playing) {
              setAutoScrollEnabled(false);
            }
          }}
          onNext={() => {
            goToNextSearchResult();
            if (player.playing) {
              setAutoScrollEnabled(false);
            }
          }}
          onClear={() => {
            setSearchTerm("");
          }}
        />

        {isCopyEnabled && onCopyTranscript && (
          <Tooltip label="Copy Transcript">
            <IconButton
              aria-label="Copy Transcript"
              icon={<CopyOutlineIcon width="18px" height="18px" />}
              color="blue.600"
              bg="white"
              variant="ghost"
              size="sm"
              onClick={onCopyTranscript}
            />
          </Tooltip>
        )}
      </Flex>
      <TranscriptSegments
        callId={callId}
        transcript={transcript}
        autoScrollEnabled={autoScrollEnabled}
        speakers={speakers}
        player={player}
        notesToHighlight={notesToHighlight}
        setAutoScrollEnabled={setAutoScrollEnabled}
        onCreateClipClick={onCreateClipClick}
        speakerOptions={speakerOptions}
        canChangeSpeaker={canChangeSpeaker}
        isCopyEnabled={isCopyEnabled}
        calculateSegmentPositions={calculateSegmentPositions}
        matchPositions={matchPositions}
        segmentPositions={segmentPositions}
        activeMatchIndex={activeMatchIndex}
        activeMatchPosition={activeMatchPosition}
        pt="12px"
      />
    </Flex>
  );
};

export default Transcript;
